import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaEnvelopeOpenText } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const POMISCalculator = () => {
  const [principal, setPrincipal] = useState(900000);
  const [accountType, setAccountType] = useState('single'); // single or joint
  const [interestRate, setInterestRate] = useState(7.4); // Current POMIS rate
  const [result, setResult] = useState(null);

  const maxLimit = accountType === 'single' ? 900000 : 1500000;

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [principal, interestRate, accountType]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Adjust principal if it exceeds new max limit
    if (principal > maxLimit) {
      setPrincipal(maxLimit);
    }
  }, [accountType, maxLimit, principal]);

  const calculate = () => {
    // POMIS: 5-year tenure, monthly interest payout
    const tenure = 5;
    const p = Number(principal) || 0;
    const rate = (Number(interestRate) || 0) / 100;
    const monthlyInterest = (p * rate) / 12;
    const annualInterest = p * rate;
    
    const monthlyData = [];
    for (let month = 1; month <= tenure * 12; month++) {
      monthlyData.push({
        month,
        year: Math.ceil(month / 12),
        interestPaid: Math.round(monthlyInterest),
      });
    }
    
    const totalInterest = annualInterest * tenure;
    const totalPayout = p + totalInterest;
    
    setResult({
      monthlyInterest: Math.round(monthlyInterest),
      annualInterest: Math.round(annualInterest),
      totalInterest: Math.round(totalInterest),
      totalPayout: Math.round(totalPayout),
      monthlyData,
    });
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('Post Office Monthly Income Scheme Report', {
      'Account Type': accountType === 'single' ? 'Single' : 'Joint',
      'Investment Amount': formatCurrency(principal),
      'Interest Rate': `${interestRate}% p.a.`,
      'Tenure': '5 years',
      'Monthly Income': formatCurrency(result.monthlyInterest),
      'Annual Income': formatCurrency(result.annualInterest),
      'Total Interest (5 years)': formatCurrency(result.totalInterest),
      'Total Payout': formatCurrency(result.totalPayout),
    });
  };

  const handleReset = () => {
    setPrincipal(accountType === 'single' ? 900000 : 1500000);
    setInterestRate(7.4);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="POMIS Calculator"
        description="Free Post Office Monthly Income Scheme Calculator - Calculate POMIS returns with monthly interest payout."
        keywords="POMIS calculator, Post Office Monthly Income Scheme calculator, POMIS interest rate, monthly income scheme, POMIS returns"
        canonicalPath="/pomis"
      />
      <div className="calculator-header">
        <h1><FaEnvelopeOpenText style={{ color: '#0ea5e9' }} /> Post Office Monthly Income Scheme (POMIS)</h1>
        <p>Calculate guaranteed monthly income from Post Office savings</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Account Type</label>
            <div className="tabs" style={{ marginBottom: '12px' }}>
              <button 
                className={`tab ${accountType === 'single' ? 'active' : ''}`}
                onClick={() => setAccountType('single')}
              >
                Single (Max ₹9L)
              </button>
              <button 
                className={`tab ${accountType === 'joint' ? 'active' : ''}`}
                onClick={() => setAccountType('joint')}
              >
                Joint (Max ₹15L)
              </button>
            </div>
          </div>

          <div className="input-group">
            <label>Investment Amount (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={principal === '' ? '' : principal}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Math.min(Number(e.target.value), maxLimit))}
                min="1000"
                max={maxLimit}
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>
              Min: ₹1,000, Max: {formatCurrency(maxLimit)} ({accountType} account)
            </small>
            <div className="slider-container">
              <input
                type="range"
                min="1000"
                max={maxLimit}
                step="1000"
                value={principal || 0}
                onChange={(e) => setPrincipal(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Interest Rate (% p.a.)</label>
            <input
              type="number"
              value={interestRate === '' ? '' : interestRate}
              onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="15"
              step="0.1"
            />
            <small style={{ color: 'var(--text-secondary)' }}>Current rate: 7.4% (Q1 FY 2024-25)</small>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #0ea5e9, #0284c7)' }}>
                <h3>Monthly Income</h3>
                <div className="amount">{formatCurrency(result.monthlyInterest)}</div>
                <small>Guaranteed every month for 5 years</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#ecfdf5', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Annual Income</span>
                  <span className="value" style={{ color: '#059669', fontSize: '1.2em' }}>{formatCurrency(result.annualInterest)}</span>
                </div>
                <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Total Interest (5 Yrs)</span>
                  <span className="value" style={{ color: '#2563eb', fontSize: '1.2em' }}>{formatCurrency(result.totalInterest)}</span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Principal Invested</span>
                  <span className="value">{formatCurrency(principal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Account Type</span>
                  <span className="value">{accountType === 'single' ? 'Single' : 'Joint'}</span>
                </div>
                <div className="result-item">
                  <span className="label">Interest Rate</span>
                  <span className="value">{interestRate}% p.a.</span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7' }}>
                  <span className="label">Total Payout (5 years)</span>
                  <span className="value" style={{ fontWeight: 'bold' }}>{formatCurrency(result.totalPayout)}</span>
                </div>
              </div>

              {/* Yearly Summary */}
              <div className="breakup-section">
                <h4>Yearly Income Summary</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Monthly Income</th>
                      <th>Annual Income</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3, 4, 5].map((year) => (
                      <tr key={year}>
                        <td>Year {year}</td>
                        <td>{formatCurrency(result.monthlyInterest)}</td>
                        <td style={{ color: '#10b981', fontWeight: 'bold' }}>{formatCurrency(result.annualInterest)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#dbeafe', fontWeight: 'bold' }}>
                      <td colSpan="2">Total Interest (5 years)</td>
                      <td style={{ color: '#2563eb' }}>{formatCurrency(result.totalInterest)}</td>
                    </tr>
                    <tr style={{ background: '#fef3c7', fontWeight: 'bold' }}>
                      <td colSpan="2">Principal + Interest</td>
                      <td>{formatCurrency(result.totalPayout)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 POMIS Key Features</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li>Guaranteed monthly income</li>
              <li>Single: Max ₹9 lakh</li>
              <li>Joint: Max ₹15 lakh</li>
              <li>5-year tenure</li>
              <li>Interest paid on 1st of each month</li>
            </ul>
          </div>
          <div>
            <ul>
              <li>No tax benefit on investment</li>
              <li>Interest is taxable</li>
              <li>Premature withdrawal after 1 year (with penalty)</li>
              <li>Can be linked to Post Office Savings Account</li>
              <li>Nomination facility available</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POMISCalculator;
